/**
 * Subscription Service
 * Handles API calls for subscription management and usage tracking
 */

class SubscriptionService {
  constructor() {
    this.baseURL = import.meta.env.VITE_API_URL || '';
  }

  /**
   * Make authenticated API request
   */
  async makeRequest(endpoint, options = {}) {
    // Add cache-busting timestamp to URL for subscription endpoints
    const cacheBuster = endpoint.includes('/subscription') ? `?t=${Date.now()}` : '';
    const url = `${this.baseURL}${endpoint}${cacheBuster}`;
    
    const defaultOptions = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      cache: 'no-store', // Force no caching
    };

    const finalOptions = { ...defaultOptions, ...options };

    try {
      const response = await fetch(url, finalOptions);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || `HTTP error! status: ${response.status}`);
        error.status = response.status;
        error.data = errorData;
        throw error;
      }
      
      const data = await response.json();
      
      // Ensure we always return a consistent structure
      return {
        success: data.success !== undefined ? data.success : true,
        data: data.data || data
      };
    } catch (error) {
      console.error(`[SubscriptionService] Request failed:`, error);
      throw error;
    }
  }

  /**
   * Get current subscription details
   */
  async getCurrentSubscription() {
    try {
      return await this.makeRequest('/api/subscription/current');
    } catch (error) {
      console.error('[SubscriptionService] getCurrentSubscription failed:', error);
      // Return safe default structure
      return {
        success: false,
        data: null,
        error: error.message
      };
    }
  }

  /**
   * Get current usage data
   */
  async getCurrentUsage() {
    try {
      return await this.makeRequest('/api/subscription/usage');
    } catch (error) {
      console.error('[SubscriptionService] getCurrentUsage failed:', error);
      // Return safe default structure with free plan limits
      return {
        success: false,
        data: {
          total_itineraries: 0,
          monthly_itineraries: 0,
          monthly_limit: 5,
          annual_limit: 60,
          plan_name: 'free',
          can_create: true,
          usage_percentage: 0
        },
        error: error.message
      };
    }
  }

  /**
   * Check if user can create itinerary
   */
  async canCreateItinerary() {
    try {
      return await this.makeRequest('/api/subscription/can-create-itinerary');
    } catch (error) {
      console.error('[SubscriptionService] canCreateItinerary failed:', error);
      // Default to allowing creation on error
      return {
        success: false,
        data: { canCreate: true },
        error: error.message
      };
    }
  }

  /**
   * Get upgrade recommendations
   */
  async getUpgradeRecommendations() {
    try {
      return await this.makeRequest('/api/subscription/upgrade-recommendations');
    } catch (error) {
      console.error('[SubscriptionService] getUpgradeRecommendations failed:', error);
      return {
        success: false,
        data: {
          should_upgrade: false,
          current_plan: 'free',
          usage_percentage: 0
        },
        error: error.message
      };
    }
  }
}

// Export singleton instance
export default new SubscriptionService();
